import type { Thread } from '@atlas/api-contract';
import { CheckCircle2, Monitor, Reply } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { getWidthPreset, presetForViewportWidth } from '@/features/viewer/width-presets';
import { formatAbsoluteTime, formatRelativeTime } from '@/lib/format/date';
import { cn } from '@/lib/utils';

import {
  authorInitial,
  firstLine,
  threadFirstComment,
  threadReplyCount,
  toThreadScreenLabel,
} from './thread-display';

export interface ThreadPanelRowProps {
  readonly thread: Thread;
  /** The version's own URL path, stripped from the screen label. */
  readonly basePath: string | null;
  /** The version's entry file, for naming the screen served by the bare URL. */
  readonly entryPath: string | null;
  readonly isSelected: boolean;
  readonly isHighlighted: boolean;
  readonly onSelect: (thread: Thread) => void;
  readonly onHover: (threadId: string | null) => void;
}

/**
 * One row of the thread panel (spec R6.1).
 *
 * Everything on it answers a question a reviewer asks while scanning: *who*
 * (initial and name), *what* (the first line), *how much conversation* (reply
 * count), *where* (screen and the width it was pinned at), *when* (relative
 * time) and *is it done* (resolved). The first line is truncated to two lines
 * rather than one, because a review comment's opening sentence is usually the
 * whole point and cutting it at forty characters turns the panel into a list of
 * fragments.
 *
 * The viewport badge is there because the same pin means different things at
 * different widths — a comment about an overlapping label at 390 is not a
 * comment about the desktop layout — and it doubles as a warning that selecting
 * this thread will offer to change the frame.
 *
 * Hover is wired both ways (spec R6.3): entering the row highlights the pin
 * through `atlas:highlight-pin`, and a pin hovered on the canvas highlights the
 * row through `isHighlighted`. Focus does the same as hover, so a keyboard
 * reviewer gets the link too.
 */
export const ThreadPanelRow = ({
  thread,
  basePath,
  entryPath,
  isSelected,
  isHighlighted,
  onSelect,
  onHover,
}: ThreadPanelRowProps) => {
  const first = threadFirstComment(thread);
  const replies = threadReplyCount(thread);
  const resolved = thread.resolvedAt !== null;
  const preset = getWidthPreset(presetForViewportWidth(thread.viewportWidth));

  return (
    <button
      type="button"
      aria-current={isSelected ? 'true' : undefined}
      className={cn(
        'w-full rounded-lg border p-2.5 text-left transition-colors',
        isSelected
          ? 'border-primary/40 bg-accent'
          : isHighlighted
            ? 'border-pin/50 bg-accent/60'
            : 'border-border hover:border-foreground/20 hover:bg-accent/50',
        resolved && !isSelected ? 'opacity-70' : '',
      )}
      onClick={() => {
        onSelect(thread);
      }}
      onMouseEnter={() => {
        onHover(thread.id);
      }}
      onMouseLeave={() => {
        onHover(null);
      }}
      onFocus={() => {
        onHover(thread.id);
      }}
      onBlur={() => {
        onHover(null);
      }}
    >
      <div className="flex items-center gap-2">
        <Avatar className="size-5 shrink-0">
          <AvatarFallback
            className={cn(
              'text-[10px] font-semibold',
              resolved
                ? 'bg-pin-resolved text-pin-resolved-foreground'
                : 'bg-pin text-pin-foreground',
            )}
          >
            {authorInitial(thread.createdByName)}
          </AvatarFallback>
        </Avatar>

        <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">
          {thread.createdByName}
        </span>

        <time
          className="shrink-0 text-xs text-muted-foreground"
          dateTime={thread.createdAt}
          title={formatAbsoluteTime(thread.createdAt)}
        >
          {formatRelativeTime(thread.createdAt)}
        </time>
      </div>

      <p
        className={cn(
          'mt-1.5 line-clamp-2 text-sm',
          first === null ? 'italic text-muted-foreground' : 'text-foreground',
        )}
      >
        {first === null ? 'No comments.' : firstLine(first.body)}
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span className="max-w-[9rem] truncate font-mono">
          {toThreadScreenLabel(thread.screenPath, basePath, entryPath)}
        </span>

        <Badge
          variant="outline"
          className="gap-1 border-border px-1.5 py-0 text-[10px] font-normal text-muted-foreground"
        >
          <Monitor aria-hidden="true" className="size-2.5" />
          {preset.label}
        </Badge>

        {replies > 0 ? (
          <span className="flex items-center gap-1">
            <Reply aria-hidden="true" className="size-3" />
            {replies} {replies === 1 ? 'reply' : 'replies'}
          </span>
        ) : null}

        {resolved ? (
          <span className="ml-auto flex items-center gap-1 text-pin-resolved">
            <CheckCircle2 aria-hidden="true" className="size-3" />
            Resolved
          </span>
        ) : null}
      </div>
    </button>
  );
};
